(() => {
  const copy = document.currentScript?.dataset || {};
  const normalize = value => String(value || '').replace(/\s+/g, ' ').trim();
  let form = null;
  let active = false;
  let submitted = false;
  let exitConfirmed = false;
  let pending = false;
  let sheet = null;
  let closeButton = null;
  const mobileViewport = window.matchMedia('(max-width: 768px), (pointer: coarse)');
  let viewportForm = null;
  let viewportFrame = 0;
  let viewportHeight = 0;
  let revealTimer = 0;
  let allowFocusReveal = true;
  const observer = new MutationObserver(() => { dismissValidationNotices(); schedule(); });

  function visible(node) {
    if (!node?.isConnected || node.closest('[hidden], [aria-hidden="true"]')) return false;
    if (node.tagName === 'DIALOG' && !node.open) return false;
    return node.getClientRects().length > 0;
  }

  function cancelFocusReveal() {
    clearTimeout(revealTimer);
    revealTimer = 0;
    allowFocusReveal = false;
  }

  function revealFocusedField() {
    revealTimer = 0;
    const viewport = window.visualViewport;
    if (!allowFocusReveal || !visible(viewportForm) || sheet?.open || (viewport && Math.abs(viewport.scale - 1) > 0.01)) return;
    const field = document.activeElement;
    const scroller = viewportForm.querySelector('.rsi-form-main');
    if (!scroller?.contains(field) || !field.matches('input:not([type="hidden"]), select, textarea') || !visible(field)) return;
    const bounds = scroller.getBoundingClientRect();
    const input = field.getBoundingClientRect();
    const viewportTop = viewport?.offsetTop || 0;
    const top = Math.max(bounds.top, viewportTop) + 12;
    const bottom = Math.min(bounds.bottom, viewportTop + (viewport?.height || window.innerHeight)) - 12;
    if (bottom <= top) return;
    const delta = input.top < top ? input.top - top : (input.bottom > bottom ? input.bottom - bottom : 0);
    if (delta) scroller.scrollTop = Math.max(0, Math.min(scroller.scrollHeight - scroller.clientHeight, scroller.scrollTop + delta));
  }

  function queueFocusReveal() {
    clearTimeout(revealTimer);
    if (allowFocusReveal) revealTimer = setTimeout(revealFocusedField, 120);
  }

  function updateViewport() {
    viewportFrame = 0;
    if (!visible(viewportForm) || sheet?.open) return;
    const viewport = window.visualViewport;
    // Preserve native pinch zoom/panning; never treat zoom as a keyboard.
    if (viewport && Math.abs(viewport.scale - 1) > 0.01) {
      clearTimeout(revealTimer);
      return;
    }
    const height = Math.round(viewport?.height || window.innerHeight);
    const top = Math.max(0, Math.round(viewport?.offsetTop || 0));
    if (height <= 0) return;
    for (const [property, value] of [['--ma-cod-viewport-height', `${height}px`], ['--ma-cod-viewport-top', `${top}px`]]) {
      if (viewportForm.style.getPropertyValue(property) !== value) viewportForm.style.setProperty(property, value);
    }
    if (!viewportForm.classList.contains('ma-cod-viewport')) viewportForm.classList.add('ma-cod-viewport');
    if (viewportHeight && height < viewportHeight - 2) queueFocusReveal();
    viewportHeight = height;
  }

  function queueViewport() {
    if (viewportForm && !viewportFrame) viewportFrame = requestAnimationFrame(updateViewport);
  }

  function stopViewport() {
    window.visualViewport?.removeEventListener('resize', queueViewport);
    window.visualViewport?.removeEventListener('scroll', queueViewport);
    window.removeEventListener('resize', queueViewport);
    window.removeEventListener('orientationchange', queueViewport);
    cancelAnimationFrame(viewportFrame);
    viewportFrame = 0;
    cancelFocusReveal();
    if (viewportForm) {
      viewportForm.classList.remove('ma-cod-viewport');
      viewportForm.style.removeProperty('--ma-cod-viewport-height');
      viewportForm.style.removeProperty('--ma-cod-viewport-top');
    }
    viewportForm = null;
    viewportHeight = 0;
  }

  function syncViewport() {
    const target = active && mobileViewport.matches && form?.matches('dialog.rsi-form-wrapper-dialog[open]') && form.querySelector('.rsi-form-main') ? form : null;
    if (target !== viewportForm) {
      stopViewport();
      viewportForm = target;
      if (target) {
        allowFocusReveal = true;
        window.visualViewport?.addEventListener('resize', queueViewport, {passive:true});
        window.visualViewport?.addEventListener('scroll', queueViewport, {passive:true});
        window.addEventListener('resize', queueViewport, {passive:true});
        window.addEventListener('orientationchange', queueViewport, {passive:true});
        queueFocusReveal();
      }
    }
    queueViewport();
  }

  function shippingValue() {
    const row = [...(form?.querySelectorAll('.rsi-total-summary-line-item') || [])].find(item => /^shipping\s*:?$/i.test(normalize(item.querySelector('.rsi-total-summary-label')?.textContent)));
    return normalize(row?.querySelector('.rsi-total-summary-value')?.textContent);
  }

  function freeShipping() {
    return /^free$/i.test(shippingValue());
  }

  function text(node, value) {
    if (node.textContent !== value) node.textContent = value;
  }

  function hasFieldErrors(root) {
    return [...root.querySelectorAll('.rsi-text-input-error, .rsi-select-error, .rsi-checkbox-error, .rsi-radio-error, .rsi-shipping-rates-error')].some(node => {
      const owner = node.closest('.rsi-text-input-wrapper, .rsi-select-wrapper');
      return normalize(node.textContent) && !node.closest('[hidden], [aria-hidden="true"]') && visible(owner || node);
    });
  }

  function syncFieldErrors() {
    form.querySelectorAll('.rsi-text-input-error, .rsi-select-error').forEach(error => {
      if (normalize(error.textContent) && !error.hasAttribute('role')) error.setAttribute('role', 'alert');
    });
  }

  function resetFailedSubmission() {
    if (!submitted || !visible(form)) return;
    const submit = form.querySelector('.rsi-submit_button');
    if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true' || submit.getAttribute('aria-busy') === 'true') return;
    if (hasFieldErrors(form)) submitted = false;
  }

  function dismissValidationNotices() {
    const root = document.getElementById('rsi_form_wrapper');
    const expected = normalize(copy.validationNotice);
    if (!expected || !visible(root)) return;
    const hasFieldError = hasFieldErrors(root);
    root.querySelectorAll('.rsi-notification-wrapper > .rsi-notification-item').forEach(notice => {
      const messages = notice.querySelectorAll('p');
      const matches = hasFieldError && messages.length === 1 && normalize(messages[0].textContent) === expected && !notice.querySelector('h4');
      if (!matches) {
        if (notice.classList.contains('ma-cod-redundant-validation')) notice.classList.remove('ma-cod-redundant-validation');
        return;
      }
      const dismiss = notice.querySelector('button');
      if (!dismiss || dismiss.disabled || normalize(dismiss.textContent) !== '✕' || notice.classList.contains('ma-cod-redundant-validation')) return;
      notice.classList.add('ma-cod-redundant-validation');
      dismiss.type = 'button';
      dismiss.click();
    });
  }

  function dismissSheet(restore = true) {
    if (!sheet) return;
    const old = sheet;
    sheet = null;
    old.close();
    old.remove();
    if (restore && visible(closeButton)) closeButton.focus({preventScroll:true});
    queueViewport();
  }

  function updateSheet() {
    if (!sheet) return;
    const names = [...new Set([...form.querySelectorAll('.rsi-product-item-name')].filter(visible).map(node => normalize(node.textContent)).filter(Boolean))];
    const hasProduct = names.length === 1 && copy.exitMessage?.includes('{product_name}');
    const message = hasProduct ? copy.exitMessage.replaceAll('{product_name}', () => names[0]) : copy.neutralMessage;
    const messageNode = sheet.querySelector('[data-ma-exit-message]');
    if (messageNode.dataset.message !== message) {
      messageNode.dataset.message = message;
      messageNode.classList.toggle('ma-cod-exit-callout', hasProduct);
      if (hasProduct) {
        messageNode.replaceChildren();
        copy.exitMessage.split('{product_name}').forEach((part, index) => {
          if (index > 0) {
            const productName = document.createElement('strong');
            productName.className = 'ma-cod-exit-product';
            productName.textContent = names[0];
            messageNode.append(productName);
          }
          if (part) {
            const warning = document.createElement('span');
            warning.className = 'ma-cod-exit-warning';
            warning.textContent = part;
            messageNode.append(warning);
          }
        });
      } else text(messageNode, message);
    }
    text(sheet.querySelector('[data-ma-exit-benefit]'), freeShipping() ? copy.freeBenefit : copy.neutralBenefit);
  }

  function openSheet(button) {
    if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return false;
    closeButton = button;
    sheet = document.createElement('dialog');
    sheet.className = 'ma-cod-exit';
    sheet.setAttribute('aria-labelledby', 'ma-cod-exit-heading');
    sheet.setAttribute('aria-describedby', 'ma-cod-exit-message ma-cod-exit-help');
    const heading = document.createElement('h2');
    heading.id = 'ma-cod-exit-heading';
    heading.textContent = copy.exitHeading;
    const message = document.createElement('p');
    message.id = 'ma-cod-exit-message';
    message.dataset.maExitMessage = '';
    const benefit = document.createElement('p');
    benefit.className = 'ma-cod-exit-benefit';
    benefit.dataset.maExitBenefit = '';
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'ma-cod-exit-continue';
    keep.textContent = copy.continueLabel;
    keep.addEventListener('click', () => { exitConfirmed = false; dismissSheet(); });
    const exit = document.createElement('button');
    exit.type = 'button';
    exit.className = 'ma-cod-exit-leave';
    exit.textContent = copy.exitLabel;
    exit.addEventListener('click', () => {
      exitConfirmed = true;
      dismissSheet();
    });
    const help = document.createElement('p');
    help.id = 'ma-cod-exit-help';
    help.className = 'ma-cod-exit-help';
    help.textContent = copy.help;
    sheet.append(heading, message, benefit, keep, exit, help);
    sheet.addEventListener('cancel', event => { event.preventDefault(); exitConfirmed = false; dismissSheet(); });
    document.body.append(sheet);
    updateSheet();
    try {
      sheet.showModal();
      keep.focus({preventScroll:true});
      return true;
    } catch {
      sheet.remove();
      sheet = null;
      return false;
    }
  }

  function resetCompact() {
    form?.querySelectorAll('[data-ma-cod-checkout-row]').forEach(node => node.remove());
    form?.querySelectorAll('.ma-cod-native-quantity, .ma-cod-native-summary').forEach(node => node.classList.remove('ma-cod-native-quantity', 'ma-cod-native-summary'));
  }

  function compact() {
    form.classList.toggle('ma-cod-compact', copy.compact === 'true');
    if (copy.compact !== 'true') return;
    const rows = form.querySelectorAll('.rsi-product-item-wrapper');
    const summary = form.querySelector('.rsi-total-summary-wrapper');
    const total = normalize(summary?.querySelector('.rsi-total-summary-total-value')?.textContent);
    const submit = form.querySelector('.rsi-submit_button');
    const layout = submit?.closest('.rsi-layout-wrapper');
    let submitItem = submit;
    while (submitItem && submitItem.parentElement !== layout) submitItem = submitItem.parentElement;
    const native = rows.length === 1 ? rows[0].querySelector('.rsi-product-item-quantity-controls') : null;
    const buttons = native?.querySelectorAll('button');
    const input = native?.querySelector('.rsi-quantity-input');
    const extraRows = [...(summary?.querySelectorAll('.rsi-total-summary-line-item') || [])].some(row => !/^(shipping|sub[\s-]?total)\s*:?$/i.test(normalize(row.querySelector('.rsi-total-summary-label')?.textContent)));
    if (!layout || !submitItem || !total || !native || buttons?.length !== 2 || !input || extraRows || summary.querySelector('input:not([type="hidden"]),select,textarea,button')) {
      resetCompact();
      return;
    }
    let bar = form.querySelector('[data-ma-cod-checkout-row]');
    if (!bar || bar.parentElement !== layout) {
      resetCompact();
      bar = document.createElement('div');
      bar.dataset.maCodCheckoutRow = '';
      bar.className = 'ma-cod-checkout-row';
      const quantity = document.createElement('div');
      quantity.className = 'ma-cod-quantity';
      quantity.setAttribute('role', 'group');
      quantity.setAttribute('aria-label', copy.quantity);
      for (let index = 0; index < 3; index++) {
        const element = document.createElement(index === 1 ? 'output' : 'button');
        if (index === 1) {
          element.dataset.maQuantityValue = '';
          element.setAttribute('aria-label', copy.quantity);
          element.setAttribute('aria-live', 'polite');
        } else {
          element.type = 'button';
          element.textContent = index === 0 ? '−' : '+';
          element.setAttribute('aria-label', index === 0 ? copy.decrease : copy.increase);
          element.dataset.maQuantityDirection = index === 0 ? '0' : '1';
          element.addEventListener('click', () => {
            const controls = form?.querySelector('.rsi-product-item-quantity-controls');
            const original = controls?.querySelectorAll('button')[Number(element.dataset.maQuantityDirection)];
            if (original && !original.disabled && original.getAttribute('aria-disabled') !== 'true') original.click();
            schedule();
          });
        }
        quantity.append(element);
      }
      const box = document.createElement('div');
      box.className = 'ma-cod-total';
      box.setAttribute('aria-live', 'polite');
      const label = document.createElement('span');
      label.textContent = copy.total;
      const amount = document.createElement('strong');
      amount.dataset.maTotalValue = '';
      const shipping = document.createElement('small');
      shipping.dataset.maShippingValue = '';
      box.append(label, amount, shipping);
      bar.append(quantity, box);
      layout.insertBefore(bar, submitItem);
    }
    native.classList.add('ma-cod-native-quantity');
    const summaryItem = summary.closest('.rsi-layout-wrapper > div');
    const summaryOnly = summaryItem && !summaryItem.querySelector('input:not([type="hidden"]),select,textarea,button') && normalize(summaryItem.textContent) === normalize(summary.textContent);
    form.querySelectorAll('.ma-cod-native-summary').forEach(node => node.classList.remove('ma-cod-native-summary'));
    (summaryOnly ? summaryItem : summary).classList.add('ma-cod-native-summary');
    text(bar.querySelector('[data-ma-quantity-value]'), input.value || '1');
    bar.querySelectorAll('[data-ma-quantity-direction]').forEach(button => {
      const original = buttons[Number(button.dataset.maQuantityDirection)];
      const disabled = original.disabled || original.getAttribute('aria-disabled') === 'true' || input.disabled;
      if (button.disabled !== disabled) button.disabled = disabled;
    });
    text(bar.querySelector('[data-ma-total-value]'), total);
    const shipping = shippingValue();
    const shippingNode = bar.querySelector('[data-ma-shipping-value]');
    text(shippingNode, freeShipping() ? copy.freeShipping : (shipping ? `${copy.shipping} ${shipping}` : ''));
    shippingNode.hidden = !shipping;
  }

  function refresh() {
    pending = false;
    observer.disconnect();
    const next = document.getElementById('rsi_form_wrapper');
    const changed = next !== form;
    if (changed) {
      dismissSheet(false);
      form = next;
      active = false;
      submitted = false;
      exitConfirmed = false;
    }
    if (form) {
      const now = visible(form);
      if (now && !active) { submitted = false; exitConfirmed = false; }
      if (!now) { exitConfirmed = false; dismissSheet(false); }
      active = now;
      syncFieldErrors();
      resetFailedSubmission();
      dismissValidationNotices();
      compact();
      if (sheet && !form.querySelector('.rsi-submit_button')) dismissSheet(false);
      updateSheet();
    }
    syncViewport();
    if (form) observer.observe(form, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['open','hidden','aria-hidden','disabled','aria-disabled','aria-busy','value','class','style']});
  }

  function schedule() {
    if (!pending) { pending = true; requestAnimationFrame(refresh); }
  }

  function start() {
    if (mobileViewport.addEventListener) mobileViewport.addEventListener('change', schedule);
    else mobileViewport.addListener(schedule);
    document.addEventListener('focusin', event => {
      if (!viewportForm?.contains(event.target) || !event.target.matches('input, select, textarea')) return;
      allowFocusReveal = true;
      queueViewport();
      queueFocusReveal();
    });
    // Manual scrolling wins over automatic focus assistance. No touch events are blocked.
    for (const type of ['pointerdown', 'touchmove', 'wheel']) {
      document.addEventListener(type, event => {
        if (!viewportForm?.contains(event.target)) return;
        if (type === 'pointerdown' && event.target instanceof Element && event.target.closest('input, select, textarea')) {
          // Retapping the same input can reopen its keyboard without focusin.
          allowFocusReveal = true;
          queueViewport();
          queueFocusReveal();
        } else cancelFocusReveal();
      }, {passive:true, capture:true});
    }
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const targetForm = event.target.closest('#rsi_form_wrapper');
      if (!targetForm) return;
      if (targetForm !== form) refresh();
      if (event.target.closest('.rsi-submit_button')) {
        submitted = true;
        dismissSheet(false);
        return;
      }
      if (event.target.closest('.rsi-product-item-quantity-controls')) schedule();
      const button = event.target.closest('.rsi-form-close-button');
      if (button && sheet?.open) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const submit = form?.querySelector('.rsi-submit_button');
      if (button) resetFailedSubmission();
      if (!button || exitConfirmed || submitted || copy.exitEnabled !== 'true' || !visible(form) || !visible(submit) || submit.disabled || submit.getAttribute('aria-disabled') === 'true' || submit.getAttribute('aria-busy') === 'true' || !form.querySelector('#first_name, #address')) return;
      if (openSheet(button)) {
        active = true;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('submit', event => {
      if (form?.contains(event.target)) { submitted = true; dismissSheet(false); }
    }, true);
    document.addEventListener('invalid', event => {
      if (form?.contains(event.target)) { submitted = false; schedule(); }
    }, true);
    document.addEventListener('input', event => { if (form?.contains(event.target)) schedule(); });
    document.addEventListener('close', event => {
      if (event.target === form) { active = false; exitConfirmed = false; dismissSheet(false); schedule(); }
    }, true);
    new MutationObserver(records => {
      dismissValidationNotices();
      if (records.some(record => !form?.contains(record.target) && !sheet?.contains(record.target))) schedule();
    }).observe(document.body, {childList:true,subtree:true});
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();






